import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, toHandlers as _toHandlers, mergeProps as _mergeProps, createElementVNode as _createElementVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withKeys as _withKeys, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { id: "main-content" }
const _hoisted_2 = {
  class: "modal",
  id: "modal-loading"
}
const _hoisted_3 = { id: "top-content" }
const _hoisted_4 = { id: "left-buttons" }
const _hoisted_5 = { class: "fv-header" }
const _hoisted_6 = { id: "center-buttons" }
const _hoisted_7 = { id: "right-buttons" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { id: "options" }
const _hoisted_11 = { id: "options-top-row" }
const _hoisted_12 = {
  key: 0,
  id: "options-content"
}
const _hoisted_13 = { id: "bottom-content" }
const _hoisted_14 = {
  key: 0,
  id: "body-logos"
}
const _hoisted_15 = {
  key: 0,
  id: "coming-soon"
}
const _hoisted_16 = { class: "video-wrapper" }
const _hoisted_17 = { class: "text-list mx-5" }
const _hoisted_18 = { key: 0 }
const _hoisted_19 = { class: "text-list mx-5" }
const _hoisted_20 = { id: "user-experience-footer" }

import { storeToRefs } from "pinia";
import { ref, reactive, computed, watch, onMounted, nextTick } from "vue";
import { useFullscreen } from "./composables/useFullscreen";
import { D2R, H2R, distance } from "@wwtelescope/astro";
import { Circle, Folder, Imageset, Place, WWTControl } from "@wwtelescope/engine";
import { ImageSetType, Thumbnail } from "@wwtelescope/engine-types";
import { GotoRADecZoomParams, engineStore } from "@wwtelescope/engine-pinia";
import { API_BASE_URL, BackgroundImageset, skyBackgroundImagesets, supportsTouchscreen, blurActiveElement, useWWTKeyboardControls, type UserExperienceRating } from "@cosmicds/vue-toolkit";
import { v4 } from "uuid";
import { RUBIN_COLORS } from "../plugins/vuetify";
import { useDisplay, useTheme } from "vuetify";
import { type ItemSelectionType } from "./types";

import { useTrackedElements } from "./composables/useTrackedElements";
import { copyPlace } from "./wwt-hacks";
type SheetType = "text" | "video";
type CameraParams = Omit<GotoRADecZoomParams, "instant">;
export interface RubinFirstLookProps {
  wwtNamespace?: string;
  initialCameraParams?: CameraParams;
}

const UUID_KEY = "cds-rubin-first-look-uuid";
const RATING_OPT_OUT_KEY = "cds-rubin-first-look-rating-optout";
const imageAPlacesURL = "https://data1.wwtassets.org/noirlab2521a/a_places.wtml";
const imageBPlacesURL= "https://data1.wwtassets.org/noirlab2521b/b_places.wtml";
type Mode = "a" | "b";
const INFOBOX_ZOOM_CUTOFF = 25;
const SMALL_LABELS_ZOOM = 25;
interface Offset {
  raOff: number;
  decOff: number;
  rollDeg?: number;
}

type OffsetRecords = Record<string, Offset>;
const infoFraction = 34;

export default /*@__PURE__*/_defineComponent({
  __name: 'RubinFirstLook',
  props: {
    wwtNamespace: { default: "rubin-first-look" },
    initialCameraParams: {}
  },
  setup(__props: any) {

const searchParams = new URLSearchParams(window.location.search);
const kiosk = searchParams.get("kiosk")?.toLowerCase() === "true";
if (kiosk) {
  document.body.classList.add("kiosk");
}

const store = engineStore();
const { raRad, decRad, zoomDeg } = storeToRefs(store);

useWWTKeyboardControls(store);

const fullscreen = useFullscreen();
const hideUI = computed(() => fullscreen.value && !kiosk);

const touchscreen = supportsTouchscreen();
// TODO: Determine this in a better way
const display = useDisplay();
const theme = useTheme();



const breakpoints = [
  [15, 3.5, "~ Rubin Field of View (3.5 degrees)"],
  [4, 3.5, "Rubin Field of View (3.5 degrees)"],
  [1, 0.5, "Full Moon (0.5 degrees)"],
  [0.25, 5 / 60, "JWST Field of View (5 arcmin)"],
  [5 / 60, 1 / 60, "1 arcmin"],
  [1 / 60, 30 / 3600, "0.5 arcmin"],
  [0, 1 / 3600, "1 arcsec"],
];

/* Properties related to device/screen characteristics */
const smallSize = computed(() => {
  return display.smAndDown.value && (display.height.value > 1.2 * display.width.value);
});

const question = Math.random() > 0.5 ? 
  "Does this spark your curiosity?" :
  "Are you learning something new?";
const currentRating = ref<UserExperienceRating | null>(null);
const currentComments = ref<string | null>(null);
const showRating = ref(false);
const showPrivacyPolicy = ref(false);
const ratingOptedOut = ref(false);
const STORY_RATING_URL = `${API_BASE_URL}/rubin-first-look/user-experience`;
const maybeUUID = window.localStorage.getItem(UUID_KEY);
const existingUser = maybeUUID !== null;
const uuid = maybeUUID ?? v4();
if (!existingUser) {
  window.localStorage.setItem(UUID_KEY, uuid);
}

const backgroundImagesets = reactive<BackgroundImageset[]>([]);
const sheet = ref<SheetType | null>(null);
const layersLoaded = ref(false);
const positionSet = ref(false);
const zooming = ref(false);
const opacity = ref(100);

let zoomTimeout: ReturnType<typeof setTimeout> | null = null;
// See https://rubin.canto.com/g/RubinVisualIdentity/index?viewIndex=0
const accentColor = computed(() => theme.global.current.value.colors.primary);
const buttonColor = computed(() => theme.global.current.value.colors.primaryVariant);
const thumbnailColor = computed(() => theme.global.current.value.colors.info);
const highlightColor = computed(() => theme.global.current.value.colors.accent);
const textColor = computed(() => theme.global.current.value.colors["on-background"]);
const tab = ref(0);

const selectedItem = ref<Thumbnail | null>(null);

const lowerLevelPlaces: Place[] = [];
const topLevelPlaces: Place[] = [];
const highlightsA = ref(new Folder());
const highlightsB = ref(new Folder());
const currentPlace = ref<Place | null>(null);
const mainImageInView = ref(false);

const mode = ref<Mode>("a");
const folder = computed(() => (mode.value == "a" && imagesLoaded.value[0]) ? highlightsA.value : highlightsB.value);

let circle: Circle | null = null;
const showOptions = ref(false);
const showCircle = ref(false);
const showLabels = ref(false);
const showScalebar = ref(!smallSize.value);
const showSlider = ref(false);
const showConstellations = ref(false);
const highlightPlaceFromZoom = computed(() => zoomDeg.value < INFOBOX_ZOOM_CUTOFF);
const showPlaceHighlights = computed(() => !showTextSheet.value && currentPlace.value !== null && highlightPlaceFromZoom.value);
const atTopLevel = computed(() => zoomDeg.value > SMALL_LABELS_ZOOM);

const _ute = useTrackedElements("", store);

// Offsets are in RA/Dec 
// If you pass them to :offset-x, or :offset-y instead
// offset ra will be how left/right the label is offset and 
// offset dec will be how up/down the label is offset
const offsets = ref<OffsetRecords>({});

function rotateOffsetToScreen(offset: Offset): Offset {
  // Rotate the offset to the screen orientation
  const cosRoll = Math.cos((offset.rollDeg ?? 0) * D2R);
  const sinRoll = Math.sin((offset.rollDeg ?? 0) * D2R);
  return {
    raOff: offset.raOff * cosRoll + offset.decOff * sinRoll,
    decOff: -offset.raOff * sinRoll + offset.decOff * cosRoll,
  };
}


fetch("https://data1.wwtassets.org/noirlab2521/offsets.json")
  .then(response => response.json())
  .then(data => {
    const keys = Object.keys(data);
    keys.forEach(key => {
      const offset = data[key] as Offset;
      // Rotate the offset to the screen orientation
      data[key] = rotateOffsetToScreen(offset);
    });
    offsets.value = data as OffsetRecords;
    
  })
  .catch(error => {
    console.error("Error fetching offsets:", error);
  });
  
const imagesLoaded = ref<[boolean, boolean]>([false, false]);
const showComingSoon = ref(false);
watch(imagesLoaded, (newValue) => {
  showComingSoon.value = !newValue[0] && !newValue[1];
}, { immediate: true });

// onBeforeMount(() => {
//   ratingOptedOut.value = window.localStorage.getItem(RATING_OPT_OUT_KEY)?.toLowerCase() === "true";
// });

onMounted(() => {
  store.waitForReady().then(async () => {
    // window.addEventListener('contextmenu', function(event) {
    //   event.stopImmediatePropagation();
    // }, true);
    skyBackgroundImagesets.forEach(iset => backgroundImagesets.push(iset));
    const loadPromises = [imageAPlacesURL, imageBPlacesURL].map(url => {
      return store.loadImageCollection({
        url,
        loadChildFolders: false,
      });
    });
    Promise.all(loadPromises).then(loadedFolders => {
      loadedFolders.forEach((loadedFolder, index) => {
        const children = loadedFolder.get_children();
        const highlightsFolder = new Folder();
        children?.forEach((item, index) => {
          if (item instanceof Place) {
            if (index === 0) {
              highlightsFolder.addChildPlace(item);
              topLevelPlaces.push(item);
            } else {
              if (item.get_names().length == 1) {
                highlightsFolder.addChildPlace(item);
              }
              lowerLevelPlaces.push(item);
            }
          }
        });
        const highlightsRef = index === 0 ? highlightsA : highlightsB;
        highlightsRef.value = highlightsFolder;
        const length = highlightsRef.value.get_children()?.length ?? 0;
        imagesLoaded.value = imagesLoaded.value.map((loaded, i) => i === index ? length > 0 : loaded) as [boolean, boolean];
      });
    }).then(() => {
      positionSet.value = true;
      layersLoaded.value = true;
      gotoMainImage(mode.value, true);
    });
    
    store.loadImageCollection({
      url: "bg.wtml",
      loadChildFolders: false,
    }).then(bgFolder => {
      const children = bgFolder.get_children();
      if (children !== null) {
        const item = children[0];
        if (item instanceof Imageset) {
          store.setBackgroundImageByName(item.get_name());
        }
      }
    });

    updateClosestPlace();

    // ratingDisplaySetup();
  });
});

async function _ratingDisplaySetup() {
  if (ratingOptedOut.value) {
    return;
  }

  const existingDataResponse = await fetch(`${STORY_RATING_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
  });

  const existingDataContent = await existingDataResponse.json();
  const alreadyAnswered = existingDataResponse.status === 200 && existingDataContent.ratings?.length > 0;

  if (alreadyAnswered) {
    return;
  }

  setTimeout(() => {
    showRating.value = true;
  }, 40_000);
}

function updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
  const body: Record<string, unknown> = {
    uuid,
    question,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    story_name: "rubin-first-look",
  };
  if (rating) {
    body.rating = rating;
  }
  if (comments) {
    body.comments = comments;
  }
  fetch(STORY_RATING_URL, {
    method: "PUT",
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

function onOptOutClicked() {
  showRating.value = false;
  ratingOptedOut.value = true;
}

function findClosest(places: Place[]): Place | null {
  let closest = currentPlace.value;
  let closestDist = closest === null ? null : distanceFromCenter(closest);

  places.forEach(place => {
    const dist = distanceFromCenter(place);
    if ((!isNaN(dist)) && ((closestDist === null) || (dist < closestDist))) {
      closest = place;
      closestDist = dist;
    }
  });

  return closest !== null && placeInView(closest) ? closest : null;
}

const closestPlace = ref<Place | null>(null);
function updateClosestPlace() {
  currentPlace.value = findClosest(atTopLevel.value ? topLevelPlaces : lowerLevelPlaces);
  closestPlace.value = currentPlace.value;
}

const forceShowCircle = ref(false);
const _onMarkerHover = (place: Place, show: boolean) => {
  forceShowCircle.value = show;
  currentPlace.value = show ? place : closestPlace.value; 
};

function updateCircle(place: Place | null) {
  if (!highlightPlaceFromZoom.value || place === null) {
    if (!forceShowCircle.value) {
      circle?.set_opacity(0);
      return;
    }
  }

  if (circle === null) {
    circle = new Circle();
    circle.set_id("infobox");
    circle.set_lineWidth(3);
    circle.set_lineColor("#ffffff");
    circle.set_skyRelative(true);
    store.addAnnotation(circle);
  }

  circle.set_opacity(showCircle.value ? 1 : 0);
  circle.setCenter(place.get_RA() * 15, place.get_dec());
  circle.set_radius(place?.angularSize);
}

function gotoMainImage(image: Mode, instant=false) {
  const index = image === "a" ? 0 : 1;
  const item = copyPlace(topLevelPlaces[index]);
  item.set_zoomLevel(zoomLevelForPlace(item) * 1.2);
  store.gotoTarget({
    place: item,
    noZoom: false,
    instant,
    trackObject: false,
  });
}

function wwtSmallestFov() {
  const renderContext = WWTControl.singleton.renderContext;
  const fovH = renderContext.get_fovAngle() * D2R;
  const fovW = fovH * renderContext.width / renderContext.height;
  return Math.min(fovW, fovH);
}

function zoomLevelForPlace(place: Place): number {
  // we want the wwt zoom level  to be same as the places .get_zoomLevel()
  const {width, height} = WWTControl.singleton.renderContext;
  // console.log(width, height, place.get_zoomLevel()/6 * D2R, wwtSmallestFov());
  if (width > height) {
    // landscape
    return place.get_zoomLevel();
  }
  return place.get_zoomLevel() * (height / width);
}

function distanceFromCenter(place: Place): number {
  return distance(place.get_RA() * H2R, place.get_dec() * D2R, raRad.value, decRad.value);
}

function placeInView(place: Place, fraction=0.0): boolean {
  // checks if the center of place is in the current field of view
  // Assume the Zoom level corresponds to the size of the image
  // fraction is ~fraction of the place that must be in the current FOV
  // by default, allow 1/2 of the place to be visible and still be considered in view
  if (place == null) {
    return false;
  }
  const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
  if (iset == null) {
    return false;
  }

  let dist = distanceFromCenter(place);
  const isetFov = (place.get_zoomLevel() / 6) * D2R;
  const curFov = wwtSmallestFov();

  dist += (fraction - 0.5) * isetFov;
  return dist < curFov / 2;
}

function handleSelection(item: Thumbnail, selection: ItemSelectionType) {
  if (item instanceof Imageset) {
    store.setForegroundImageByName(item.get_name());
    const type = item.get_dataSetType();
    if (type === ImageSetType.planet) {
      store.setBackgroundImageByName(item.get_name());
    }
  } else if (item instanceof Place) {
    const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
    if (imageset !== null) {
      store.setForegroundImageByName(imageset.get_name());
    }

    const instant = selection === "dblclick";
    if (selection !== "folder") {

      store.gotoTarget({
        place: item,
        noZoom: false,
        instant,
        trackObject: true,
      });
    }
  }  

  selectedItem.value = item;
}

const ready = computed(() => layersLoaded.value && positionSet.value);

/* `isLoading` is a bit redundant here, but it could potentially have independent logic */
const isLoading = computed(() => !ready.value);

const infoSheetLocation = computed(() => smallSize.value ? "bottom" : "right");
const infoSheetHeight = computed(() => infoSheetLocation.value === "bottom" ? `${infoFraction}%` : "100%");
const infoSheetWidth = computed(() => infoSheetLocation.value === "bottom" ? "100%" : `${infoFraction}%`);
const infoTextHeight = computed(() => infoSheetLocation.value === "bottom" ? `calc(${infoFraction}vh - 25px)` : "calc(100vh - 25px)");
const infoSheetTransition = computed(() => infoSheetLocation.value === "bottom" ? "dialog-bottom-transition" : "tab-reverse-transition");

/* This lets us inject component data into element CSS */
const cssVars = computed(() => {
  const rubinColors = Object.keys(RUBIN_COLORS).reduce((acc: Record<string, string>, key: string) => {
    acc[`--${key}`] = RUBIN_COLORS[key];
    return acc;
  }, {});
  return {
    ...rubinColors,
    "--accent-color": accentColor.value,
    "--button-color": buttonColor.value,
    "--thumbnail-color": thumbnailColor.value,
    "--app-content-height": showTextSheet.value && infoSheetLocation.value === "bottom" ? `${100 - infoFraction}vh` : "100dvh",
    "--app-content-width": showTextSheet.value && infoSheetLocation.value === "right" ? `${100 - infoFraction}vw` : "100dvw",
    "--info-sheet-width": infoSheetWidth.value,
    "--info-sheet-height": infoSheetHeight.value,
    "--info-text-height": infoTextHeight.value,
  };
});



  
/**
  Computed flags that control whether the relevant dialogs display.
  The `sheet` data member stores which sheet is open, so these are just
  computed wrappers around modifying/querying that which can be used as
  dialog v-model values
*/
const showTextSheet = computed({
  get() {
    return sheet.value === "text";
  },
  set(_value: boolean) {
    selectSheet("text");
  }
});

const showVideoSheet = computed({
  get() {
    return sheet.value === "video";
  },
  set(value: boolean) {
    selectSheet("video");
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    }
  }
});

/**
  This is convenient if there's any other logic that we want to run
  when the splash screen is closed
*/
function closeSplashScreen() {
  // showSplashScreen.value = false;
}

function selectSheet(sheetType: SheetType | null) {
  if (sheet.value === sheetType) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = sheetType;
  }
}

function onMove() {
  if (placeInView(topLevelPlaces[0])) {
    mode.value = "a";
    mainImageInView.value = true;
  } else if (placeInView(topLevelPlaces[1])) {
    mode.value = "b";
    mainImageInView.value = true;
  } else {
    mainImageInView.value = false;
  }
  updateClosestPlace();
}

function onZoomChange(newZoom: number, oldZoom: number) {
  zooming.value = Math.abs((newZoom - oldZoom) / oldZoom) > 0.01;
  updateClosestPlace();
  updateCircle(currentPlace.value);
  if (zooming.value) {
    zoomTimeout = setTimeout(() => {
      zooming.value = false;
    }, 100);
  } else if (zoomTimeout) {
    clearTimeout(zoomTimeout);
  }

}

watch(raRad, (_ra: number) => onMove());
watch(decRad, (_dec: number) => onMove());
watch(zoomDeg, onZoomChange);
watch(showCircle, (_show: boolean) => updateCircle(currentPlace.value));
watch(showConstellations, (show: boolean) => {
  store.applySetting(["showConstellationFigures", show]);
  store.applySetting(["showConstellationLabels", show]);
});
watch(currentPlace, updateCircle);
watch(mode, (newMode: Mode) => {
  theme.global.name.value = newMode === "a" ? "rubinA" : "rubinB";
  opacity.value = 100;
});
watch(opacity, store.setForegroundOpacity);

watch(ratingOptedOut, (optOut: boolean) => {
  window.localStorage.setItem(RATING_OPT_OUT_KEY, optOut.toString());
});

return (_ctx: any,_cache: any) => {
  const _component_WorldWideTelescope = _resolveComponent("WorldWideTelescope")!
  const _component_wwt_tracked_content = _resolveComponent("wwt-tracked-content")!
  const _component_splash_screen = _resolveComponent("splash-screen")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_folder_view = _resolveComponent("folder-view")!
  const _component_v_slider = _resolveComponent("v-slider")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_scalebar = _resolveComponent("scalebar")!
  const _component_credit_logos = _resolveComponent("credit-logos")!
  const _component_infobox = _resolveComponent("infobox")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_funding_acknowledgement = _resolveComponent("funding-acknowledgement")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_user_experience = _resolveComponent("user-experience")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_cds_privacy_policy = _resolveComponent("cds-privacy-policy")!
  const _component_v_app = _resolveComponent("v-app")!
  const _directive_hide = _resolveDirective("hide")!

  return (_openBlock(), _createBlock(_component_v_app, {
    id: "app",
    style: _normalizeStyle(cssVars.value),
    class: _normalizeClass([kiosk ? 'kiosk' : ''])
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_WorldWideTelescope, { "wwt-namespace": __props.wwtNamespace }, null, 8, ["wwt-namespace"]),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(lowerLevelPlaces, (place, index) => {
          return _createVNode(_component_wwt_tracked_content, {
            key: index,
            place: place,
            name: place.get_name(),
            "offset-ra": offsets.value[place.get_name()]?.raOff ?? 0,
            "offset-dec": offsets.value[place.get_name()]?.decOff ?? 0,
            store: _unref(store),
            visible: showLabels.value && !atTopLevel.value,
            onClick: ($event: any) => (handleSelection(place, 'click')),
            onDblclick: ($event: any) => (handleSelection(place, 'dblclick'))
          }, {
            default: _withCtx((props) => [
              _createElementVNode("div", _mergeProps({
                class: ["tracked-places", {"star": place.angularSize < 0.02}]
              }, _toHandlers(props.on, true)), _toDisplayString(place.get_name()), 17)
            ]),
            _: 2
          }, 1032, ["place", "name", "offset-ra", "offset-dec", "store", "visible", "onClick", "onDblclick"])
        }), 64)),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(topLevelPlaces, (place, index) => {
          return _createVNode(_component_wwt_tracked_content, {
            key: index,
            place: place,
            name: place.get_name(),
            "offset-ra": offsets.value[place.get_name()]?.raOff ?? 0,
            "offset-dec": offsets.value[place.get_name()]?.decOff ?? 0,
            store: _unref(store),
            visible: showLabels.value && atTopLevel.value,
            onClick: ($event: any) => (handleSelection(place, 'click')),
            onDblclick: ($event: any) => (handleSelection(place, 'dblclick'))
          }, {
            default: _withCtx((props) => [
              _createElementVNode("div", _mergeProps({ class: "tracked-places top-level-place" }, _toHandlers(props.on, true)), _toDisplayString(place.get_name()), 17)
            ]),
            _: 2
          }, 1032, ["place", "name", "offset-ra", "offset-dec", "store", "visible", "onClick", "onDblclick"])
        }), 64)),
        _createVNode(_component_splash_screen, {
          title: "Rubin First Look",
          cssVars: cssVars.value,
          onClose: closeSplashScreen
        }, null, 8, ["cssVars"]),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_2, [...(_cache[29] || (_cache[29] = [
              _createElementVNode("div", { class: "container" }, [
                _createElementVNode("div", { class: "spinner" }),
                _createElementVNode("p", null, "Loading …")
              ], -1)
            ]))], 512), [
              [_vShow, isLoading.value]
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_3, [
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_4, [
            _withDirectives(_createVNode(_component_folder_view, {
              class: _normalizeClass(['folder-view', smallSize.value ? 'folder-view-tall' : '']),
              "root-folder": folder.value,
              "background-color": accentColor.value,
              "thumbnail-color": thumbnailColor.value,
              "highlight-color": highlightColor.value,
              "text-color": textColor.value,
              "flex-direction": "column",
              onSelect: _cache[0] || (_cache[0] = ({ item, type }) => handleSelection(item, type)),
              "start-expanded": !smallSize.value
            }, {
              header: _withCtx(({ toggleExpanded, expanded }) => [
                _createElementVNode("div", _hoisted_5, [
                  _cache[30] || (_cache[30] = _createElementVNode("span", null, "Explore", -1)),
                  _createVNode(_component_font_awesome_icon, {
                    icon: expanded ? 'chevron-up' : 'chevron-down',
                    onClick: ($event: any) => (toggleExpanded()),
                    onKeyup: _withKeys(($event: any) => (toggleExpanded()), ["enter"]),
                    tabindex: "0",
                    "aria-role": "button",
                    "aria-pressed": expanded,
                    "aria-hidden": "false",
                    "aria-label": "Toggle folder view"
                  }, null, 8, ["icon", "onClick", "onKeyup", "aria-pressed"])
                ])
              ]),
              _: 1
            }, 8, ["class", "root-folder", "background-color", "thumbnail-color", "highlight-color", "text-color", "start-expanded"]), [
              [_vShow, folder.value.get_children()?.length ?? 0 > 0]
            ])
          ])), [
            [_directive_hide, hideUI.value]
          ]),
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
            (!smallSize.value && showSlider.value && mainImageInView.value)
              ? (_openBlock(), _createBlock(_component_v_slider, {
                  key: 0,
                  modelValue: opacity.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((opacity).value = $event)),
                  min: 0,
                  max: 100,
                  color: "secondary",
                  class: "opacity-slider",
                  "hide-details": ""
                }, {
                  prepend: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                    _createTextVNode(" NOIRLab All-Sky/DSS ", -1)
                  ]))]),
                  append: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(store).foregroundImageset?.get_name()), 1)
                  ]),
                  _: 1
                }, 8, ["modelValue"]))
              : _createCommentVNode("", true)
          ])), [
            [_directive_hide, hideUI.value]
          ]),
          _createElementVNode("div", _hoisted_7, [
            (!_unref(fullscreen) && topLevelPlaces.length > 1)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass([{'go-to-a': mode.value == 'b', 'go-to-b': mode.value == 'a'}]),
                  id: "goto-other-image",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (gotoMainImage((mode.value == 'a') ? 'b' : 'a', false))),
                  onDblclick: _cache[3] || (_cache[3] = ($event: any) => (gotoMainImage((mode.value == 'a') ? 'b' : 'a', true)))
                }, " Go to " + _toDisplayString(mode.value == 'a' ? 'the' : '') + " " + _toDisplayString(topLevelPlaces[mode.value=='a'? 1 : 0]?.get_name()), 35))
              : _createCommentVNode("", true),
            _createElementVNode("div", null, [
              (!hideUI.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createVNode(_component_icon_button, {
                      id: "info-icon",
                      modelValue: showTextSheet.value,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showTextSheet).value = $event)),
                      "fa-icon": "info",
                      color: buttonColor.value,
                      "tooltip-text": "Show information",
                      "tooltip-location": "start"
                    }, null, 8, ["modelValue", "color"])
                  ]))
                : _createCommentVNode("", true),
              (!(hideUI.value || kiosk))
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(_component_icon_button, {
                      modelValue: showVideoSheet.value,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showVideoSheet).value = $event)),
                      "fa-icon": "video",
                      color: buttonColor.value,
                      "tooltip-text": "Watch video",
                      "tooltip-location": "start"
                    }, null, 8, ["modelValue", "color"])
                  ]))
                : _createCommentVNode("", true),
              (!(kiosk && _unref(fullscreen)))
                ? (_openBlock(), _createBlock(_component_icon_button, {
                    key: 2,
                    id: "fullscreen-icon",
                    onActivate: _cache[6] || (_cache[6] = ($event: any) => (fullscreen.value = !_unref(fullscreen))),
                    "fa-icon": _unref(fullscreen) ? 'compress' : 'expand',
                    color: buttonColor.value,
                    "tooltip-text": `${_unref(fullscreen) ? 'Exit' : 'Make'} fullscreen`,
                    "tooltip-location": "start"
                  }, null, 8, ["fa-icon", "color", "tooltip-text"]))
                : _createCommentVNode("", true),
              _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_component_icon_button, {
                    id: "options-toggle",
                    "fa-icon": showOptions.value ? 'chevron-up' : 'sliders',
                    color: buttonColor.value,
                    onActivate: _cache[7] || (_cache[7] = ($event: any) => (showOptions.value = !showOptions.value)),
                    tabindex: "0",
                    border: false,
                    "background-color": "transparent"
                  }, null, 8, ["fa-icon", "color"])
                ]),
                (showOptions.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _createVNode(_component_v_checkbox, {
                        modelValue: showLabels.value,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showLabels).value = $event)),
                        label: "Object Labels",
                        color: buttonColor.value,
                        "hide-details": "",
                        density: "compact"
                      }, null, 8, ["modelValue", "color"]),
                      _createVNode(_component_v_checkbox, {
                        modelValue: showCircle.value,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showCircle).value = $event)),
                        label: "Region Circle",
                        color: buttonColor.value,
                        "hide-details": "",
                        density: "compact"
                      }, null, 8, ["modelValue", "color"]),
                      _createVNode(_component_v_checkbox, {
                        modelValue: showScalebar.value,
                        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showScalebar).value = $event)),
                        label: "Scale Bar",
                        color: buttonColor.value,
                        "hide-details": "",
                        density: "compact"
                      }, null, 8, ["modelValue", "color"]),
                      (!smallSize.value)
                        ? (_openBlock(), _createBlock(_component_v_checkbox, {
                            key: 0,
                            modelValue: showSlider.value,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((showSlider).value = $event)),
                            label: "Opacity Slider",
                            color: buttonColor.value,
                            "hide-details": "",
                            density: "compact"
                          }, null, 8, ["modelValue", "color"]))
                        : _createCommentVNode("", true),
                      false
                        ? (_openBlock(), _createBlock(_component_v_checkbox, {
                            key: 1,
                            modelValue: showConstellations.value,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showConstellations).value = $event)),
                            label: "Constellations",
                            color: buttonColor.value,
                            "hide-details": "",
                            density: "compact"
                          }, null, 8, ["modelValue", "color"]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ])), [
                [_directive_hide, hideUI.value]
              ])
            ])
          ])
        ]),
        _withDirectives(_createElementVNode("div", {
          class: _normalizeClass(['selected-info', smallSize.value ? 'selected-info-tall' : ''])
        }, null, 2), [
          [_vShow, showPlaceHighlights.value]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createVNode(_component_scalebar, {
            width: 1920,
            breakpoints: breakpoints,
            visible: showScalebar.value && !zooming.value,
            class: "scalebar"
          }, null, 8, ["visible"]),
          (!smallSize.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createVNode(_component_credit_logos, {
                  "default-logos": ['cosmicds', 'wwt'],
                  "extra-logos": [
              {
                alt: 'INTUITIVE Planetarium at the U.S. Space & Rocket Center',
                src: './SpaceRocketCenterIntuitivePlanetarium-Logo-small.png',
                href: 'https://www.rocketcenter.com/INTUITIVEPlanetarium',
                name: 'INTUITIVE'
              },
              {
                alt: 'Vera C. Rubin Observatory',
                src: './rubin_white_2.png',
                href: 'https://rubinobservatory.org/',
                name: 'RubinObservatory'
              }
            ]
                })
              ]))
            : _createCommentVNode("", true),
          _withDirectives(_createVNode(_component_infobox, {
            class: _normalizeClass([{'with-scalebar': showScalebar.value}, {'small-size': smallSize.value}]),
            place: currentPlace.value,
            small: smallSize.value
          }, null, 8, ["class", "place", "small"]), [
            [_directive_hide, hideUI.value]
          ]),
          _cache[32] || (_cache[32] = _createElementVNode("div", { class: "perma-credit" }, "Image Credit: RubinObs/NOIRLab/SLAC/NSF/DOE/AURA", -1))
        ])), [
          [_directive_hide, hideUI.value]
        ]),
        (!imagesLoaded.value[0] && !imagesLoaded.value[1] && showComingSoon.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createVNode(_component_font_awesome_icon, {
                id: "close-coming-soon",
                icon: "times",
                size: "lg",
                onClick: _cache[13] || (_cache[13] = ($event: any) => (showComingSoon.value = false)),
                onKeyup: _cache[14] || (_cache[14] = _withKeys(($event: any) => (showComingSoon.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[33] || (_cache[33] = _createElementVNode("div", { class: "coming-soon-text" }, [
                _createElementVNode("h1", null, "Coming Soon!"),
                _createElementVNode("p", null, [
                  _createTextVNode(" Please "),
                  _createElementVNode("strong", null, "REFRESH"),
                  _createTextVNode(" this page after June 23, 2025, 11:20am EDT to load the Rubin First Look imagery. ")
                ]),
                _createElementVNode("p", null, " For now, you can explore this full-sky image from NOIRLab and the Digital Sky Survey and get used to navigating around the sky in WorldWide Telescope. ")
              ], -1))
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_v_dialog, {
          id: "video-container",
          modelValue: showVideoSheet.value,
          "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showVideoSheet).value = $event)),
          transition: "slide-y-transition",
          fullscreen: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_16, [
              _createVNode(_component_font_awesome_icon, {
                id: "video-close-icon",
                class: "close-icon",
                icon: "times",
                size: "lg",
                onClick: _cache[15] || (_cache[15] = ($event: any) => (showVideoSheet.value = false)),
                onKeyup: _cache[16] || (_cache[16] = _withKeys(($event: any) => (showVideoSheet.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[34] || (_cache[34] = _createElementVNode("iframe", {
                id: "info-video",
                width: "560",
                height: "315",
                src: "https://www.youtube.com/embed/ZsIkLuw2Y68?si=wOzU-B0malOA0kW1",
                title: "YouTube video player",
                frameborder: "0",
                allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
                referrerpolicy: "strict-origin-when-cross-origin",
                allowfullscreen: ""
              }, null, -1))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          style: _normalizeStyle(cssVars.value),
          class: _normalizeClass(['info-sheet', `info-sheet-${infoSheetLocation.value}`]),
          id: "text-info-sheet",
          "hide-overlay": "",
          persistent: "",
          "no-click-animation": "",
          absolute: "",
          scrim: false,
          location: "end",
          modelValue: showTextSheet.value,
          "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((showTextSheet).value = $event)),
          transition: infoSheetTransition.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, { height: "100%" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_tabs, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((tab).value = $event)),
                  height: "32px",
                  color: "rubin-turquoise",
                  "slider-color": "rubin-teal",
                  id: "tabs",
                  dense: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                        _createElementVNode("h3", null, "Rubin's Science", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                        _createElementVNode("h3", null, "User Guide", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(_component_font_awesome_icon, {
                  id: "close-text-icon",
                  class: "control-icon",
                  icon: "times",
                  size: "lg",
                  onClick: _cache[19] || (_cache[19] = ($event: any) => (showTextSheet.value = false)),
                  onKeyup: _cache[20] || (_cache[20] = _withKeys(($event: any) => (showTextSheet.value = false), ["enter"])),
                  tabindex: "0"
                }),
                _createVNode(_component_v_window, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((tab).value = $event)),
                  id: "tab-items",
                  class: "pb-2 no-bottom-border-radius"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                (imagesLoaded.value.some(loaded => loaded))
                                  ? (_openBlock(), _createBlock(_component_v_container, {
                                      key: 0,
                                      class: "pa-0"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[37] || (_cache[37] = _createElementVNode("p", null, [
                                          _createTextVNode(" This text is excerpted from the "),
                                          _createElementVNode("a", {
                                            href: "https://noirlab.edu/public/news/noirlab2521/",
                                            target: "_blank",
                                            rel: "noopener noreferrer"
                                          }, "Rubin First Look Press Release"),
                                          _createTextVNode(". See the full release for more information. ")
                                        ], -1)),
                                        _cache[38] || (_cache[38] = _createElementVNode("h4", { class: "user-guide-header" }, "About Rubin Observatory", -1)),
                                        _cache[39] || (_cache[39] = _createElementVNode("div", { class: "background-header-image" }, null, -1)),
                                        _cache[40] || (_cache[40] = _createElementVNode("p", null, " The NSF–DOE Vera C. Rubin Observatory, a major new scientific facility jointly funded by the U.S. National Science Foundation and the U.S. Department of Energy's Office of Science, released its first imagery on June 23, 2025 at an event in Washington, D.C. The imagery shows cosmic phenomena captured at an unprecedented scale. In just over 10 hours of test observations, NSF–DOE Rubin Observatory has already captured millions of galaxies and Milky Way stars and thousands of asteroids. The imagery is a small preview of Rubin Observatory's upcoming 10-year scientific mission to explore and understand some of the Universe's biggest mysteries. ", -1)),
                                        _cache[41] || (_cache[41] = _createElementVNode("p", null, " The result of more than two decades of work, Rubin Observatory is perched at the summit of Cerro Pachón in Chile, where dry air and dark skies provide one of the world's best observing locations. Rubin's innovative 8.4-meter telescope has the largest digital camera ever built, which feeds a powerful data processing system. Later in 2025, Rubin will begin its primary mission, the Legacy Survey of Space and Time, in which it will ceaselessly scan the sky nightly for 10 years to precisely capture every visible change. ", -1)),
                                        _cache[42] || (_cache[42] = _createElementVNode("p", null, " The result will be an ultrawide, ultra-high-definition time-lapse record of the Universe. It will bring the sky to life with a treasure trove of billions of scientific discoveries. The images will reveal asteroids and comets, pulsating stars, supernova explosions, far-off galaxies and perhaps cosmic phenomena that no one has seen before. ", -1)),
                                        _cache[43] || (_cache[43] = _createElementVNode("h4", { class: "user-guide-header" }, "Who was Vera Rubin?", -1)),
                                        _cache[44] || (_cache[44] = _createElementVNode("p", { class: "pb-5" }, " Rubin Observatory is named in honor of trailblazing U.S. astronomer Vera C. Rubin, who found conclusive evidence of vast quantities of invisible material known as dark matter. Understanding the nature of dark matter, dark energy and other large-scale cosmic mysteries is a central focus of Rubin Observatory's mission. Dark energy is what scientists call the mysterious and colossally powerful force that appears to be causing galaxies in the Universe to move away from each other at an accelerating rate. Although dark matter and dark energy collectively comprise 95% of the Universe, their properties remain unknown. ", -1)),
                                        _cache[45] || (_cache[45] = _createElementVNode("div", { class: "background-rubin-image" }, null, -1)),
                                        _cache[46] || (_cache[46] = _createElementVNode("p", null, [
                                          _createElementVNode("cite", null, [
                                            _createTextVNode(" Observatory Image Credit: RubinObs/NOIRLab/SLAC/NSF/DOE/AURA/B. Quint "),
                                            _createElementVNode("a", {
                                              href: "https://noirlab.edu/public/images/iotw2229a/",
                                              target: "_blank",
                                              rel: "noopener noreferrer"
                                            }, "Link to original")
                                          ]),
                                          _createElementVNode("br"),
                                          _createElementVNode("cite", null, [
                                            _createTextVNode(" Vera Rubin Image Credit: Carnegie Institution for Science/NOIRLab. "),
                                            _createElementVNode("a", {
                                              href: "https://noirlab.edu/public/images/VeraRubin-6-enlarged-CC/",
                                              target: "_blank",
                                              rel: "noopener noreferrer"
                                            }, "Link to original")
                                          ])
                                        ], -1)),
                                        _createVNode(_component_v_spacer, { class: "end-spacer" })
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createBlock(_component_v_container, {
                                      key: 1,
                                      class: "pa-0"
                                    }, {
                                      default: _withCtx(() => [...(_cache[47] || (_cache[47] = [
                                        _createElementVNode("p", null, " Come back June 23rd, 11:20am EDT to learn more! ", -1)
                                      ]))]),
                                      _: 1
                                    }))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, { class: "pa-0" }, {
                                  default: _withCtx(() => [
                                    _cache[76] || (_cache[76] = _createElementVNode("p", null, [
                                      _createTextVNode(" This "),
                                      _createElementVNode("a", {
                                        href: "https://www.worldwidetelescope.org/home",
                                        target: "_blank",
                                        rel: "noopener noreferrer"
                                      }, "WorldWide Telescope"),
                                      _createTextVNode(" (WWT) interactive visualization provides a contextual, deep dive into the stunning new imagery from the Rubin Observatory. ")
                                    ], -1)),
                                    _cache[77] || (_cache[77] = _createElementVNode("h4", { class: "user-guide-header" }, "Navigation & Information", -1)),
                                    _cache[78] || (_cache[78] = _createElementVNode("ul", { class: "text-list mx-5" }, [
                                      _createElementVNode("li", null, " To navigate the WWT view, use the following controls: ")
                                    ], -1)),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "mt-2 mx-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[48] || (_cache[48] = [
                                                _createTextVNode(" Pan ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "press + drag" : "click + drag"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "" : "W-A-S-D"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "keys"), 1),
                                            _cache[49] || (_cache[49] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "mx-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[50] || (_cache[50] = [
                                                _createTextVNode(" Zoom ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "pinch in and out" : "scroll in and out"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "" : "I-O"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "keys"), 1),
                                            _cache[51] || (_cache[51] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { class: "pt-0" }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("ul", _hoisted_17, [
                                              (imagesLoaded.value.every(loaded => loaded))
                                                ? (_openBlock(), _createElementBlock("li", _hoisted_18, [
                                                    _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "Tap" : "Click"), 1),
                                                    _cache[52] || (_cache[52] = _createTextVNode(" the ", -1)),
                                                    _createElementVNode("strong", null, "Go to Image " + _toDisplayString(topLevelPlaces[mode.value=='a'? 1 : 0]?.get_name()), 1),
                                                    _cache[53] || (_cache[53] = _createTextVNode(" button in the upper right to switch the view to that image. ", -1))
                                                  ]))
                                                : _createCommentVNode("", true),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "Tap" : "Click"), 1),
                                                _cache[54] || (_cache[54] = _createTextVNode(" on an object ", -1)),
                                                _cache[55] || (_cache[55] = _createElementVNode("strong", null, "Image Thumbnail", -1)),
                                                _cache[56] || (_cache[56] = _createTextVNode(" or ", -1)),
                                                _cache[57] || (_cache[57] = _createElementVNode("strong", null, "Label", -1)),
                                                _cache[58] || (_cache[58] = _createTextVNode(" to pan to that object. ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Double-" + _toDisplayString(_unref(touchscreen) ? "tap" : "click"), 1),
                                                _cache[59] || (_cache[59] = _createTextVNode(" on an object ", -1)),
                                                _cache[60] || (_cache[60] = _createElementVNode("strong", null, "Image Thumbnail", -1)),
                                                _cache[61] || (_cache[61] = _createTextVNode(" or ", -1)),
                                                _cache[62] || (_cache[62] = _createElementVNode("strong", null, "Label", -1)),
                                                _cache[63] || (_cache[63] = _createTextVNode(" to move instantly to that object. ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "Tap" : "Click"), 1),
                                                _cache[64] || (_cache[64] = _createTextVNode(" on the object ", -1)),
                                                _cache[65] || (_cache[65] = _createElementVNode("strong", null, "Information Box", -1)),
                                                _cache[66] || (_cache[66] = _createTextVNode(" in the lower left to learn more about it. ", -1))
                                              ])
                                            ])
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [
                                            _cache[72] || (_cache[72] = _createElementVNode("h4", { class: "user-guide-header" }, "Main View Buttons", -1)),
                                            _createElementVNode("ul", _hoisted_19, [
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "info",
                                                  size: "lg"
                                                }),
                                                _cache[67] || (_cache[67] = _createTextVNode(" open or close this information panel ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "video",
                                                  size: "lg"
                                                }),
                                                _cache[68] || (_cache[68] = _createTextVNode(" view a video tutorial about this visualization ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "expand",
                                                  size: "lg"
                                                }),
                                                _cache[69] || (_cache[69] = _createTextVNode(" toggle fullscreen mode ( view only the imagery with no user interface.) ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "compress",
                                                  size: "lg"
                                                }),
                                                _cache[70] || (_cache[70] = _createTextVNode(" return to the normal user interface. ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "sliders",
                                                  size: "lg"
                                                }),
                                                _cache[71] || (_cache[71] = _createTextVNode(" open more control options ", -1))
                                              ])
                                            ]),
                                            _cache[73] || (_cache[73] = _createElementVNode("h4", { class: "user-guide-header mt-5" }, "Controls", -1)),
                                            _cache[74] || (_cache[74] = _createElementVNode("ul", { class: "text-list mx-5" }, [
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Object Labels"),
                                                _createTextVNode(": Display or hide the names of objects in the view. ")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Region Markers"),
                                                _createTextVNode(": Display or hide the boxes that roughly delineate the labeled objects. ")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Scale Bar"),
                                                _createTextVNode(": Display or hide the scale bar that contextualizes how much of the sky you are seeing. ")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Opacity Slider"),
                                                _createTextVNode(": Display or hide the opacity slider that lets you compare the new Rubin imagery with a background sky from NOIRLab and the Digitized Sky Survey. ")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Constellations"),
                                                _createTextVNode(": Display or hide the constellation lines and labels to orient yourself in the sky. ")
                                              ])
                                            ], -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [
                                            _cache[75] || (_cache[75] = _createElementVNode("div", { class: "credits" }, [
                                              _createElementVNode("h4", null, "Credits:"),
                                              _createElementVNode("p", null, [
                                                _createTextVNode(" Rubin imagery and informational text provided by "),
                                                _createElementVNode("strong", null, [
                                                  _createElementVNode("a", { href: "https://noirlab.edu/" }, "NOIRLab")
                                                ]),
                                                _createTextVNode(". ")
                                              ]),
                                              _createElementVNode("h5", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.rocketcenter.com/INTUITIVEPlanetarium",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, [
                                                  _createElementVNode("em", null, "INTUITIVE"),
                                                  _createTextVNode("® Planetarium at the U.S. Space & Rocket Center")
                                                ])
                                              ]),
                                              _createElementVNode("p", null, "David Weigel"),
                                              _createElementVNode("h5", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.cosmicds.cfa.harvard.edu/",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "CosmicDS")
                                              ]),
                                              _createElementVNode("p", null, "Alyssa Goodman"),
                                              _createElementVNode("p", null, "Pat Udomprasert"),
                                              _createElementVNode("p", null, "Jon Carifio"),
                                              _createElementVNode("p", null, "John Lewis"),
                                              _createElementVNode("h5", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.worldwidetelescope.org/home",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "WorldWide Telescope")
                                              ]),
                                              _createElementVNode("p", null, "Peter Williams"),
                                              _createElementVNode("p", null, "Jon Carifio"),
                                              _createElementVNode("p", null, "David Weigel")
                                            ], -1)),
                                            _createVNode(_component_v_spacer, { class: "end-spacer" })
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_funding_acknowledgement)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "class", "modelValue", "transition"]),
        _createVNode(_component_v_container, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_expand_transition, null, {
              default: _withCtx(() => [
                (showRating.value)
                  ? (_openBlock(), _createBlock(_component_user_experience, {
                      key: 0,
                      question: _unref(question),
                      "icon-size": "3x",
                      onDismiss: _cache[25] || (_cache[25] = (_rating, _comments) => {
            showRating.value = false;
          }),
                      onRating: _cache[26] || (_cache[26] = (rating) => {
            currentRating.value = rating;
            updateUserExperienceInfo(currentRating.value, currentComments.value);
          }),
                      onFinish: _cache[27] || (_cache[27] = (rating, comments) => {
            currentRating.value = rating;
            currentComments.value = comments;
            updateUserExperienceInfo(currentRating.value, currentComments.value)
            showRating.value = false;
          })
                    }, {
                      footer: _withCtx(() => [
                        _createElementVNode("div", _hoisted_20, [
                          _createVNode(_component_v_btn, {
                            class: "rating-opt-put",
                            color: "#BDBDBD",
                            size: "small",
                            variant: "text",
                            onClick: onOptOutClicked
                          }, {
                            default: _withCtx(() => [...(_cache[79] || (_cache[79] = [
                              _createTextVNode(" Don't show again ", -1)
                            ]))]),
                            _: 1
                          }),
                          _createVNode(_component_v_btn, {
                            class: "privacy-button",
                            color: "#BDBDBD",
                            onClick: _cache[23] || (_cache[23] = ($event: any) => (showPrivacyPolicy.value = true)),
                            onKeyup: _cache[24] || (_cache[24] = _withKeys(($event: any) => (showPrivacyPolicy.value = true), ["enter"])),
                            size: "small",
                            target: "_blank",
                            rel: "noopener noreferrer"
                          }, {
                            default: _withCtx(() => [...(_cache[80] || (_cache[80] = [
                              _createTextVNode(" What is this? ", -1)
                            ]))]),
                            _: 1
                          })
                        ])
                      ]),
                      _: 1
                    }, 8, ["question"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_cds_privacy_policy, {
              modelValue: showPrivacyPolicy.value,
              "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((showPrivacyPolicy).value = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["style", "class"]))
}
}

})